// ============================================================================
// BP-04-02: Documents Frontend - DocumentCard Component
// ============================================================================

import React, { useState } from 'react';
import {
  FileText,
  Image,
  Table,
  File,
  MoreVertical,
  Download,
  Eye,
  Pencil,
  Trash2,
  FolderInput,
  Share2,
  Clock,
  User,
  Check,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { Document, DocumentCardProps } from '../types/documentsTypes';
import { DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_COLORS, FILE_TYPE_ICONS } from '../types/documentsTypes';
import { formatFileSize } from '../hooks/useDocumentUpload';

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

function getFileIcon(mimeType: string): React.ReactNode {
  const iconName = FILE_TYPE_ICONS[mimeType] || FILE_TYPE_ICONS.default;
  const iconProps = { className: 'w-8 h-8', strokeWidth: 1.5 };

  switch (iconName) {
    case 'FileText':
      return <FileText {...iconProps} />;
    case 'Image':
      return <Image {...iconProps} />;
    case 'Table':
      return <Table {...iconProps} />;
    default:
      return <File {...iconProps} />;
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

// -----------------------------------------------------------------------------
// DropdownMenu Component (inline)
// -----------------------------------------------------------------------------

interface DropdownMenuProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute right-0 top-full mt-1 z-50 min-w-[160px] bg-white rounded-lg shadow-lg border border-warm-gray/10 py-1 animate-in fade-in slide-in-from-top-2 duration-200">
        {children}
      </div>
    </>
  );
};

interface DropdownItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

const DropdownItem: React.FC<DropdownItemProps> = ({
  icon,
  label,
  onClick,
  variant = 'default',
}) => (
  <button
    onClick={onClick}
    className={cn(
      'w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors',
      variant === 'danger'
        ? 'text-red-600 hover:bg-red-50'
        : 'text-navy hover:bg-cream'
    )}
  >
    {icon}
    {label}
  </button>
);

// -----------------------------------------------------------------------------
// DocumentCard Component
// -----------------------------------------------------------------------------

export const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  isSelected = false,
  onSelect,
  onView,
  onDownload,
  onEdit,
  onDelete,
  onMove,
  onShare,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const typeColor = DOCUMENT_TYPE_COLORS[document.document_type];
  const typeLabel = DOCUMENT_TYPE_LABELS[document.document_type];

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(document.id, !isSelected);
  };

  const handleMenuAction = (action: () => void) => {
    setIsMenuOpen(false);
    action();
  };

  return (
    <div
      className={cn(
        'group relative bg-white rounded-xl border transition-all duration-200 cursor-pointer overflow-hidden',
        isSelected
          ? 'border-sage ring-2 ring-sage/20 shadow-md'
          : 'border-warm-gray/10 hover:border-sage-light hover:shadow-md',
        isHovered && 'transform -translate-y-1'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onView?.(document)}
    >
      {/* Selection checkbox */}
      {onSelect && (
        <button
          onClick={handleSelect}
          className={cn(
            'absolute top-3 left-3 z-10 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200',
            isSelected
              ? 'bg-sage border-sage text-white'
              : 'bg-white/80 border-warm-gray/30 opacity-0 group-hover:opacity-100',
            isSelected && 'opacity-100'
          )}
        >
          {isSelected && <Check className="w-3 h-3" />}
        </button>
      )}

      {/* Thumbnail / Icon area */}
      <div className="relative h-32 bg-gradient-to-br from-cream to-cream-dark flex items-center justify-center">
        {document.thumbnail_url ? (
          <img
            src={document.thumbnail_url}
            alt={document.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="p-4 rounded-xl"
            style={{ backgroundColor: `${typeColor}15`, color: typeColor }}
          >
            {getFileIcon(document.mime_type)}
          </div>
        )}

        {/* Type badge */}
        <div
          className="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded-full text-white"
          style={{ backgroundColor: typeColor }}
        >
          {typeLabel}
        </div>

        {/* Shared indicator */}
        {document.is_shared_with_attorney && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-0.5 bg-white/90 rounded-full text-xs text-navy">
            <Share2 className="w-3 h-3" />
            <span>Shared</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-medium text-navy line-clamp-2 mb-1 leading-tight">
          {document.title}
        </h3>

        {document.provider_name && (
          <p className="text-sm text-warm-gray mb-2 truncate">
            {document.provider_name}
          </p>
        )}

        {/* Meta info */}
        <div className="flex items-center gap-3 text-xs text-warm-gray">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {getRelativeTime(document.updated_at)}
          </span>
          <span>{formatFileSize(document.file_size_bytes)}</span>
        </div>

        {/* Tags */}
        {document.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {document.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-cream rounded-full text-xs text-navy-light"
              >
                {tag}
              </span>
            ))}
            {document.tags.length > 3 && (
              <span className="px-2 py-0.5 bg-cream rounded-full text-xs text-navy-light">
                +{document.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions menu */}
      <div className="absolute top-3 right-3 z-20">
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            className={cn(
              'p-1.5 rounded-lg transition-all duration-200',
              isMenuOpen
                ? 'bg-sage text-white'
                : 'bg-white/80 text-warm-gray hover:bg-white hover:text-navy opacity-0 group-hover:opacity-100'
            )}
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          <DropdownMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
            {onView && (
              <DropdownItem
                icon={<Eye className="w-4 h-4" />}
                label="View"
                onClick={() => handleMenuAction(() => onView(document))}
              />
            )}
            {onDownload && (
              <DropdownItem
                icon={<Download className="w-4 h-4" />}
                label="Download"
                onClick={() => handleMenuAction(() => onDownload(document))}
              />
            )}
            {onEdit && (
              <DropdownItem
                icon={<Pencil className="w-4 h-4" />}
                label="Edit"
                onClick={() => handleMenuAction(() => onEdit(document))}
              />
            )}
            {onMove && (
              <DropdownItem
                icon={<FolderInput className="w-4 h-4" />}
                label="Move"
                onClick={() => handleMenuAction(() => onMove(document))}
              />
            )}
            {onShare && (
              <DropdownItem
                icon={<Share2 className="w-4 h-4" />}
                label={document.is_shared_with_attorney ? 'Manage Sharing' : 'Share with Attorney'}
                onClick={() => handleMenuAction(() => onShare(document))}
              />
            )}
            {onDelete && (
              <>
                <div className="my-1 border-t border-warm-gray/10" />
                <DropdownItem
                  icon={<Trash2 className="w-4 h-4" />}
                  label="Delete"
                  onClick={() => handleMenuAction(() => onDelete(document))}
                  variant="danger"
                />
              </>
            )}
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// DocumentCardSkeleton Component
// -----------------------------------------------------------------------------

export const DocumentCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden animate-pulse">
    <div className="h-32 bg-cream-dark" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-cream-dark rounded w-3/4" />
      <div className="h-3 bg-cream-dark rounded w-1/2" />
      <div className="flex gap-2">
        <div className="h-3 bg-cream-dark rounded w-16" />
        <div className="h-3 bg-cream-dark rounded w-12" />
      </div>
    </div>
  </div>
);

export default DocumentCard;

